import { ReactNode } from "react";
import { Signal, Wifi } from "lucide-react";
import TabBar from "./TabBar";

interface AppShellProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  children: ReactNode;
  dark?: boolean;
}

const BatteryIcon = ({ dark }: { dark?: boolean }) => {
  const color = dark ? "#FAF9FC" : "#2E2A39";
  return (
    <div className="relative flex items-center" style={{ width: 27, height: 13 }}>
      <div
        className="rounded-sm"
        style={{
          width: 24,
          height: 12,
          border: `1px solid ${color}`,
          opacity: 0.35,
          padding: 1,
        }}
      >
        <div
          className="rounded-[1px]"
          style={{
            width: "85%",
            height: "100%",
            background: color,
          }}
        />
      </div>
      <div
        className="rounded-r-sm"
        style={{
          width: 2,
          height: 6,
          background: color,
          opacity: 0.4,
          marginLeft: -0.5,
        }}
      />
    </div>
  );
};

const getPageBackground = (activeTab: string) => {
  switch (activeTab) {
    case "target":
      return "bg-morning";
    case "arsenal":
      return "bg-afternoon";
    case "battle":
      return "bg-night";
    case "profile":
      return "bg-rainy";
    default:
      return "bg-morning";
  }
};

export default function AppShell({ activeTab, onTabChange, children, dark = false }: AppShellProps) {
  const isDark = false;
  const pageBgClass = getPageBackground(activeTab);

  return (
    <div className="flex items-center justify-center min-h-screen py-8">
      <div
        className="relative"
        style={{
          width: 390,
          height: 844,
          borderRadius: 55,
          background: "#1a1a1c",
          padding: 4,
          boxShadow:
            "0 25px 80px -15px rgba(46, 42, 57, 0.35), 0 10px 30px -10px rgba(46, 42, 57, 0.2)",
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            borderRadius: 53,
            background: "linear-gradient(135deg, #3a3a3e 0%, #1a1a1c 50%, #3a3a3e 100%)",
            padding: 2,
          }}
        >
          <div
            className="relative w-full h-full overflow-hidden"
            style={{
              borderRadius: 50,
              background: isDark
                ? "linear-gradient(180deg, #1a1530 0%, #232847 50%, #181327 100%)"
                : "linear-gradient(180deg, #FAFAF9 0%, #FDFCFB 100%)",
            }}
          >
            <div className="dynamic-island" />

            <div
              className="status-bar"
              style={{ color: isDark ? "#FAF9FC" : "#2E2A39" }}
            >
              <span className="text-[15px] font-semibold tracking-tight" style={{ fontFamily: '"SF Pro Text", system-ui' }}>
                9:41
              </span>
              <div className="flex items-center gap-1.5">
                <Signal size={16} strokeWidth={2.5} />
                <Wifi size={16} strokeWidth={2.5} />
                <BatteryIcon dark={isDark} />
              </div>
            </div>

            <div
              className={`absolute top-[54px] left-0 right-0 bottom-0 overflow-y-auto scrollbar-hide animate-page ${pageBgClass}`}
              style={{ paddingBottom: 110 }}
            >
              {children}
            </div>

            <TabBar activeTab={activeTab} onTabChange={onTabChange} dark={isDark} />

            <div className={`home-indicator ${isDark ? "home-indicator-dark" : ""}`} />
          </div>
        </div>
      </div>
    </div>
  );
}